// @flow

/**
 * This is a simple event-based inactivity timer that makes it possible to
 * execute any piece of code after given time without input from the user.
 * Input is defined as any mouse move or key press happening within the
 * target element (by default the document).
 *
 * This solution is based on this answer: https://stackoverflow.com/a/10126042
 */

/**
 * First off, it is necessary to define the event type to which subscribers
 * can listen
 */
export const INACTIVITY_TIMER_HIT_EVENT_TYPE = "inactivity-timer-hit";

export const setInactivityTimer = (timeoutInMinutes: number, target: EventTarget = document) => {
  let timer: ?TimeoutID = null;

  /**
   * Clearing is as simple as calling a clearTimeout - passing null timer will
   * be a no-op.
   */
  const clear = () => {
    if (timer) {
      clearTimeout(timer);
    }
  };

  /**
   * Setting and resetting the timer is basically disabling and re-enabling the
   * timer. The timer is dispatching a new event of our type after time passed
   * as the timeoutInMinutes param - of course as number of minutes.
   */
  const reset = () => {
    clear();

    timer = setTimeout(
      () => target.dispatchEvent(new Event(INACTIVITY_TIMER_HIT_EVENT_TYPE)),
      timeoutInMinutes * 60000 // ms per min
    );
  };

  /**
   * Enabling listening to user input events
   */
  target.addEventListener("mousemove", reset);
  target.addEventListener("keypress", reset);

  /**
   * First timer setting
   */
  reset();

  /**
   * Finally, return the unsubscribing callback.
   */
  return clear;
};
